import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SizeGuide, SizeGuideColumn, SizeGuideLine } from '../../models/size-guide';
import { SizeGuideCatalogEntry } from '../../models/size-guide-catalog';
import { SizeGuideService } from '../../services/size-guide.service';
import { RegexUtils } from '../../utils/regex.utils';
import { SelectComponent } from '../select/select.component';

/** Every centimeter cell (each selected measurement × each selected size) must be filled. */
function sizeGuideCellsAreFilled(control: AbstractControl): ValidationErrors | null {
    if (!(control instanceof FormGroup)) {
        return null;
    }

    const measurements = control.get('measurementCodes')?.value;
    const sizes = control.get('sizeCodes')?.value;
    if (!Array.isArray(measurements) || measurements.length === 0 || !Array.isArray(sizes) || sizes.length === 0) {
        return null;
    }

    const cellControls = Object.entries(control.controls).filter(([name]) => name.startsWith('cell-'));
    if (cellControls.length !== measurements.length * sizes.length) {
        return { valuesRequired: true };
    }

    const everyCellFilled = cellControls.every(([, child]) => {
        const value: unknown = child.value;
        return typeof value === 'string' && value.trim().length > 0;
    });

    return everyCellFilled ? null : { valuesRequired: true };
}

@Component({
    selector: 'app-size-guide-editor-form',
    standalone: true,
    templateUrl: './size-guide-editor-form.component.html',
    styleUrls: ['./size-guide-editor-form.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, TranslatePipe, SelectComponent],
})
export class SizeGuideEditorFormComponent implements OnInit, OnDestroy, OnChanges {
    @Input() existingGuide: SizeGuide | null = null;
    @Output() loadingChange = new EventEmitter<boolean>();

    form: FormGroup = new FormGroup(
        {
            name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            measurementCodes: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
            sizeCodes: new FormControl<string[]>([], { nonNullable: true, validators: [Validators.required] }),
        },
        { validators: [sizeGuideCellsAreFilled] },
    );

    measurementOptions: { label: string; value: string }[] = [];
    sizeOptions: { label: string; value: string }[] = [];
    private catalogMeasurements: SizeGuideCatalogEntry[] = [];
    private catalogSizes: SizeGuideCatalogEntry[] = [];

    lines: SizeGuideLine[] = [];
    columns: SizeGuideColumn[] = [];
    accordionOpen: Record<string, boolean> = {};

    private subs = new Subscription();
    private catalogLoaded = false;

    constructor(private readonly sizeGuideService: SizeGuideService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (!this.catalogLoaded || !changes['existingGuide']) {
            return;
        }
        if (this.existingGuide?.guide) {
            this.hydrateFromExisting();
        }
    }

    ngOnInit() {
        this.loadingChange.emit(true);
        this.subs.add(
            this.sizeGuideService.getConfig().subscribe({
                next: (config) => {
                    this.catalogMeasurements = config.measurements || [];
                    this.catalogSizes = config.sizes || [];
                    this.measurementOptions = this.catalogMeasurements.map((m) => ({ label: m.label, value: m.code }));
                    this.sizeOptions = this.catalogSizes.map((s) => ({ label: s.label, value: s.code }));
                    this.catalogLoaded = true;
                    if (this.existingGuide?.guide) {
                        this.hydrateFromExisting();
                    }
                    this.wireValueChanges();
                    this.loadingChange.emit(false);
                },
                error: () => this.loadingChange.emit(false),
            }),
        );
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    private wireValueChanges() {
        this.subs.add(
            this.form.get('measurementCodes')!.valueChanges.subscribe((codes: string[]) => {
                this.syncMeasurements(codes || []);
            }),
        );
        this.subs.add(
            this.form.get('sizeCodes')!.valueChanges.subscribe((codes: string[]) => {
                this.syncSizes(codes || []);
            }),
        );
    }

    private hydrateFromExisting() {
        const g = this.existingGuide.guide;
        this.form.get('name')!.setValue(this.existingGuide.name || '');
        const measurementCodes = (g.columns || []).map((c) => this.columnNameToCode(c.name));
        const sizeCodes = (g.lines || []).map((l) => this.lineNameToCode(l.name));
        this.mergeLegacyMeasurementLabels(measurementCodes);
        this.mergeLegacySizeLabels(sizeCodes);
        this.form.patchValue({ measurementCodes, sizeCodes }, { emitEvent: false });
        this.buildFromState(measurementCodes, sizeCodes, g.columns, g.lines);
    }

    private mergeLegacyMeasurementLabels(codes: string[]) {
        const known = new Set(this.measurementOptions.map((o) => o.value));
        const extras: { label: string; value: string }[] = [];
        for (const c of codes) {
            if (c && !known.has(c)) {
                known.add(c);
                extras.push({ label: c, value: c });
            }
        }
        if (extras.length) {
            this.measurementOptions = [...this.measurementOptions, ...extras];
        }
    }

    private mergeLegacySizeLabels(codes: string[]) {
        const known = new Set(this.sizeOptions.map((o) => o.value));
        const extras: { label: string; value: string }[] = [];
        for (const c of codes) {
            if (c && !known.has(c)) {
                known.add(c);
                extras.push({ label: c, value: c });
            }
        }
        if (extras.length) {
            this.sizeOptions = [...this.sizeOptions, ...extras];
        }
    }

    private columnNameToCode(columnName: string): string {
        const byCode = this.catalogMeasurements.find((m) => m.code === columnName);
        if (byCode) {
            return byCode.code;
        }
        const byLabel = this.catalogMeasurements.find((m) => m.label === columnName);
        return byLabel?.code ?? columnName;
    }

    private lineNameToCode(lineName: string): string {
        const byCode = this.catalogSizes.find((s) => s.code === lineName);
        if (byCode) {
            return byCode.code;
        }
        const byLabel = this.catalogSizes.find((s) => s.label === lineName);
        return byLabel?.code ?? lineName;
    }

    private buildFromState(
        measurementCodes: string[],
        sizeCodes: string[],
        existingCols?: SizeGuideColumn[],
        existingLines?: SizeGuideLine[],
    ) {
        this.removeAllDynamicControls();
        this.columns = [];
        this.lines = [];
        this.accordionOpen = {};

        measurementCodes.forEach((code, colIndex) => {
            const prev = existingCols?.[colIndex];
            const colInputId = prev?.colInputId ?? `col-${code}`;
            this.columns.push({ name: code, colInputId });
            this.form.addControl(colInputId, new FormControl(code, { nonNullable: true, validators: [Validators.required] }));
        });

        sizeCodes.forEach((code, lineIndex) => {
            const prev = existingLines?.[lineIndex];
            const lineInputId = prev?.lineInputId ?? `line-${code}`;
            const cells = measurementCodes.map((mCode, colIndex) => {
                const inputId = `cell-${code}-${mCode}`;
                const content = prev?.cells[colIndex]?.content ?? '';
                this.addCellControl(inputId, content);
                return { content, inputId };
            });
            this.lines.push({ name: code, lineInputId, cells });
            this.form.addControl(lineInputId, new FormControl(code, { nonNullable: true, validators: [Validators.required] }));
        });

        measurementCodes.forEach((code, i) => {
            this.accordionOpen[code] = i === 0;
        });
    }

    private addCellControl(inputId: string, content: string): void {
        this.form.addControl(
            inputId,
            new FormControl(content, {
                nonNullable: true,
                validators: [Validators.pattern(RegexUtils.SIZE_GUIDE_CELL_REGEX)],
            }),
        );
    }

    private removeAllDynamicControls() {
        for (const c of this.columns) {
            if (this.form.contains(c.colInputId)) {
                this.form.removeControl(c.colInputId);
            }
        }
        for (const line of this.lines) {
            if (this.form.contains(line.lineInputId)) {
                this.form.removeControl(line.lineInputId);
            }
            for (const cell of line.cells) {
                if (this.form.contains(cell.inputId)) {
                    this.form.removeControl(cell.inputId);
                }
            }
        }
    }

    private snapshotCells(): Record<string, Record<string, string>> {
        const out: Record<string, Record<string, string>> = {};
        for (const line of this.lines) {
            out[line.name] = {};
            this.columns.forEach((col, i) => {
                const cell = line.cells[i];
                if (cell && this.form.contains(cell.inputId)) {
                    out[line.name][col.name] = (this.form.get(cell.inputId)?.value as string) ?? '';
                }
            });
        }
        return out;
    }

    private syncMeasurements(codes: string[]) {
        const prevCells = this.snapshotCells();
        const lineCodes = (this.form.get('sizeCodes')!.value as string[]) || [];
        const prevLineByCode = new Map(this.lines.map((l) => [l.name, l]));

        this.removeAllDynamicControls();

        this.columns = codes.map((code) => ({
            name: code,
            colInputId: `col-${code}`,
        }));
        for (const col of this.columns) {
            this.form.addControl(col.colInputId, new FormControl(col.name, { nonNullable: true, validators: [Validators.required] }));
        }

        this.lines = lineCodes.map((lineCode: string) => {
            const prev = prevLineByCode.get(lineCode);
            const lineInputId = prev?.lineInputId ?? `line-${lineCode}`;
            const cells = codes.map((mCode) => {
                const inputId = `cell-${lineCode}-${mCode}`;
                const content = prevCells[lineCode]?.[mCode] ?? '';
                this.addCellControl(inputId, content);
                return { content, inputId };
            });
            return { name: lineCode, lineInputId, cells };
        });

        for (const line of this.lines) {
            this.form.addControl(line.lineInputId, new FormControl(line.name, { nonNullable: true, validators: [Validators.required] }));
        }

        this.updateAccordionKeys(codes);
    }

    private updateAccordionKeys(codes: string[]) {
        const next: Record<string, boolean> = {};
        codes.forEach((code, i) => {
            next[code] = this.accordionOpen[code] ?? i === 0;
        });
        if (codes.length && !codes.some((c) => next[c])) {
            next[codes[0]] = true;
        }
        this.accordionOpen = next;
    }

    private syncSizes(codes: string[]) {
        const prevCells = this.snapshotCells();
        const prevLines = [...this.lines];
        const measurementCodes = (this.form.get('measurementCodes')!.value as string[]) || [];
        this.removeAllDynamicControls();

        for (const col of this.columns) {
            this.form.addControl(col.colInputId, new FormControl(col.name, { nonNullable: true, validators: [Validators.required] }));
        }

        this.lines = codes.map((lineCode) => {
            const existing = prevLines.find((l) => l.name === lineCode);
            const lineInputId = existing?.lineInputId ?? `line-${lineCode}`;
            const cells = measurementCodes.map((mCode: string) => {
                const inputId = `cell-${lineCode}-${mCode}`;
                const content = prevCells[lineCode]?.[mCode] ?? '';
                this.addCellControl(inputId, content);
                return { content, inputId };
            });
            return { name: lineCode, lineInputId, cells };
        });

        for (const line of this.lines) {
            this.form.addControl(line.lineInputId, new FormControl(line.name, { nonNullable: true, validators: [Validators.required] }));
        }
    }

    toggleAccordion(code: string) {
        this.accordionOpen[code] = !this.accordionOpen[code];
    }

    measurementLabel(code: string): string {
        return this.catalogMeasurements.find((m) => m.code === code)?.label || code;
    }

    sizeLabel(code: string): string {
        return this.catalogSizes.find((s) => s.code === code)?.label || code;
    }

    markTouched() {
        this.form.markAllAsTouched();
    }

    isValid(): boolean {
        return this.form.valid;
    }

    getPayload(): { name: string; guideJson: string } | null {
        if (!this.form.valid) {
            return null;
        }
        const name = this.form.get('name')!.value as string;
        const linesOut: SizeGuideLine[] = this.lines.map((line) => ({
            name: (this.form.get(line.lineInputId)?.value as string) ?? line.name,
            lineInputId: line.lineInputId,
            cells: line.cells.map((cell) => ({
                content: (this.form.get(cell.inputId)?.value as string) ?? '',
                inputId: cell.inputId,
            })),
        }));
        const columnsOut: SizeGuideColumn[] = this.columns.map((col) => ({
            name: (this.form.get(col.colInputId)?.value as string) ?? col.name,
            colInputId: col.colInputId,
        }));
        return {
            name,
            guideJson: JSON.stringify({ lines: linesOut, columns: columnsOut }),
        };
    }
}
